// ─────────────────────────────────────────────────────────────────────────────
// Backup URL Integration Utilities
// Utilities to integrate backup metadata URLs with existing outbox sync pattern
// Ensures URLs are used instead of blob data in sync payloads
// ─────────────────────────────────────────────────────────────────────────────

import { CompleteSedationBackup as LocalBackupType } from '../types/backupTypes';
import { convertBackupDataForAPI } from './apiDataConverter';
import { 
  getBackupMetadataBySedationId, 
  saveBackupMetadata,
  generateBackupChecksum,
  BackupMetadataRecord 
} from '../hooks/repos/backupMetadataRepo';

/**
 * Create a clean backup payload with URLs instead of blob data
 * This integrates with the existing outbox pattern without changing it
 */
export async function createCleanBackupPayload(
  sedationId: string,
  originalBackupData: LocalBackupType
): Promise<LocalBackupType> {
  
  console.log('🔄 createCleanBackupPayload - Starting process for sedation:', sedationId);
  
  // Handle both API format (lowercase) and Local format (uppercase)
  const apiFormat = originalBackupData as any;
  const localFormat = originalBackupData as LocalBackupType;
  
  console.log('📊 Original backup data structure:', {
    hasPatient: !!(localFormat.Patient || apiFormat.patient),
    hasSedationDetail: !!(localFormat.SedationDetail || apiFormat.sedationDetail),
    hasMeasurements: !!(localFormat.SedationMeasurements || apiFormat.sedationMeasurements),
    measurementCount: (localFormat.SedationMeasurements || apiFormat.sedationMeasurements || []).length,
    hasReport: !!(localFormat.SedationReportReceipt || apiFormat.sedationReportReceipt),
    hasIntraOppState: !!(localFormat.IntraOppState || apiFormat.intraOppState),
    hasBackupMetadata: !!(localFormat.BackupMetadata || apiFormat.backupMetadata)
  });
  
  try {
    // Get backup metadata to get URLs
    const backupMetadata = await getBackupMetadataBySedationId(sedationId);
    console.log('🗄️ Retrieved backup metadata:', !!backupMetadata);
    
    if (!backupMetadata) {
      console.warn('⚠️ No backup metadata found for sedation:', sedationId);
      // Return cleaned data without URLs if no metadata exists
      const cleanedData = cleanBlobDataFromPayload(originalBackupData) as LocalBackupType;
      console.log('📤 Returning cleaned data without metadata:', {
        hasPatient: !!cleanedData.Patient,
        hasSedationDetail: !!cleanedData.SedationDetail,
        hasMeasurements: !!cleanedData.SedationMeasurements,
        measurementCount: (cleanedData.SedationMeasurements || []).length,
        hasReport: !!cleanedData.SedationReportReceipt
      });
      return cleanedData;
    }

    // Create clean payload with blobs/base64 removed
    const cleanPayload = cleanBlobDataFromPayload(originalBackupData) as LocalBackupType;
    console.log('🧹 After cleaning blob data:', {
      hasPatient: !!cleanPayload.Patient,
      hasSedationDetail: !!cleanPayload.SedationDetail,
      hasMeasurements: !!cleanPayload.SedationMeasurements,
      measurementCount: (cleanPayload.SedationMeasurements || []).length,
      hasReport: !!cleanPayload.SedationReportReceipt,
      hasIntraOppState: !!cleanPayload.IntraOppState,
      hasBackupMetadata: !!cleanPayload.BackupMetadata
    });

    // Helper to validate Azure blob URLs only
    const onlyAzureUrls = (urls: string[] | undefined | null) =>
      (urls || []).filter((url) => !!url && !url.startsWith('data:') && url.includes('blob.core.windows.net'));

    // Merge validated URLs from backup metadata into payload
    const mergedImageUrls = onlyAzureUrls(backupMetadata.ImageUrls);
    const mergedPdfUrl = backupMetadata.PdfUrl && backupMetadata.PdfUrl.includes('blob.core.windows.net')
      ? backupMetadata.PdfUrl
      : cleanPayload?.BackupMetadata?.PdfUrl;

    const payloadWithUrls: LocalBackupType = {
      ...cleanPayload,
      BackupMetadata: {
        ...cleanPayload.BackupMetadata,
        PdfUrl: mergedPdfUrl || undefined,
        ImageUrls: mergedImageUrls,
        DataBackupUrl: backupMetadata.DataBackupUrl || cleanPayload.BackupMetadata?.DataBackupUrl,
        // Add checksum if available
        DataIntegrity: {
          ...cleanPayload.BackupMetadata?.DataIntegrity,
          checksum: backupMetadata.Checksum || cleanPayload.BackupMetadata?.DataIntegrity?.checksum
        }
      }
    } as LocalBackupType;
    
    console.log('✅ Final payload with URLs:', {
      hasPatient: !!payloadWithUrls.Patient,
      hasSedationDetail: !!payloadWithUrls.SedationDetail,
      hasMeasurements: !!payloadWithUrls.SedationMeasurements,
      measurementCount: (payloadWithUrls.SedationMeasurements || []).length,
      hasReport: !!payloadWithUrls.SedationReportReceipt,
      hasIntraOppState: !!payloadWithUrls.IntraOppState,
      hasBackupMetadata: !!payloadWithUrls.BackupMetadata,
      hasPdfUrl: !!payloadWithUrls.BackupMetadata?.PdfUrl,
      imageUrlCount: (payloadWithUrls.BackupMetadata?.ImageUrls || []).length
    });
    
    return payloadWithUrls;

  } catch (error) {
    console.error('❌ Error creating clean backup payload:', error);
    // Fallback: return cleaned data without URLs
    return cleanBlobDataFromPayload(originalBackupData) as LocalBackupType;
  }
}

/**
 * Save backup metadata when creating backups
 * This should be called when uploading assets to blob storage
 */
export async function saveBackupUrlsForSync(
  patientId: string,
  sedationId: string,
  patientName: string,
  urls: {
    pdfUrl?: string;
    imageUrls?: string[];
    dataBackupUrl?: string;
  },
  originalData?: LocalBackupType
): Promise<BackupMetadataRecord> {
  
  // CRITICAL: Validate that all image URLs are Azure blob storage URLs
  const validImageUrls = (urls.imageUrls || [])
    .filter((url: string) => url && !url.startsWith('data:') && url.includes('blob.core.windows.net'));
  
  if (urls.imageUrls && urls.imageUrls.length > 0 && validImageUrls.length === 0) {
    console.warn(`⚠️ All ${urls.imageUrls.length} provided image URLs are invalid (not Azure blob storage URLs)`);
    console.warn('Invalid URLs:', urls.imageUrls);
  }
  
  if (validImageUrls.length !== (urls.imageUrls || []).length) {
    console.warn(`🧹 Filtered out ${(urls.imageUrls || []).length - validImageUrls.length} invalid image URLs`);
  }
  
  
  const backupData = {
    PatientId: patientId,
    SedationId: sedationId,
    PatientName: patientName,
    BackupVersion: "1.0",
    GeneratedAt: new Date().toISOString(),
    BackupType: 'complete' as const,
    
    // URLs from blob storage uploads - only valid Azure URLs
    PdfUrl: urls.pdfUrl || null,
    ImageUrls: validImageUrls, // Only validated Azure blob URLs
    DataBackupUrl: urls.dataBackupUrl || null,
    
    // Statistics from original data
    MeasurementsCount: getMeasurementCount(originalData),
    ImagesCount: urls.imageUrls?.length || 0,
    SessionDataSize: originalData ? JSON.stringify(originalData).length : 0,
    
    // Data integrity flags
    HasCompletePatientData: !!(originalData as any)?.patient || !!(originalData as any)?.Patient,
    HasCompleteSedationDetail: !!(originalData as any)?.sedationDetail || !!(originalData as any)?.SedationDetail,
    HasIntraOpData: hasIntraOpData(originalData),
    HasMeasurements: getMeasurementCount(originalData) > 0,
    HasReport: !!urls.pdfUrl,
    
    // Initial sync status
    SyncStatus: 'pending' as const,
    SyncAttempts: 0,
    LastSyncAttempt: null,
    SyncError: null,
    
    // Base entity fields
    ModifiedDateTime: new Date().toISOString(),
    Synced: false,
    Deactivated: false,
    LastSyncedAt: null,
    PendingSync: 'create' as const,
    
    // Checksum for integrity
    Checksum: generateBackupChecksum({ 
      patientId, 
      sedationId, 
      urls, 
      timestamp: Date.now() 
    })
  };
  
  const savedMetadata = await saveBackupMetadata(backupData);
  
  return savedMetadata;
}

/**
 * Remove blob data from any object to prevent API failures
 * CRITICAL: This function MUST preserve all essential data structures
 * Only removes actual blob/binary content, never removes structured data
 */
function cleanBlobDataFromPayload(data: any): any {
  if (!data) return data;
  
  // FIXED: Use proper deep clone that preserves data structure
  const cleaned = deepCloneAndCleanBlobs(data);
  
  console.log('🧹 cleanBlobDataFromPayload - Data structure check:', {
    originalHasMeasurements: !!(data.SedationMeasurements || data.sedationMeasurements),
    originalMeasurementCount: (data.SedationMeasurements || data.sedationMeasurements || []).length,
    originalHasReport: !!(data.SedationReportReceipt || data.sedationReportReceipt),
    originalHasPatient: !!(data.Patient || data.patient),
    originalHasSedationDetail: !!(data.SedationDetail || data.sedationDetail),
    cleanedHasMeasurements: !!(cleaned.SedationMeasurements || cleaned.sedationMeasurements),
    cleanedMeasurementCount: (cleaned.SedationMeasurements || cleaned.sedationMeasurements || []).length,
    cleanedHasReport: !!(cleaned.SedationReportReceipt || cleaned.sedationReportReceipt),
    cleanedHasPatient: !!(cleaned.Patient || cleaned.patient),
    cleanedHasSedationDetail: !!(cleaned.SedationDetail || cleaned.sedationDetail)
  });
  
  return cleaned;
}

/**
 * Deep clone an object while removing only blob/binary data
 * Preserves ALL structural data including arrays, objects, etc.
 */
function deepCloneAndCleanBlobs(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  
  // Handle primitive types
  if (typeof obj !== 'object') {
    // Only remove blob-like string data
    if (typeof obj === 'string' && obj.startsWith('data:')) {
      console.log('🧹 Removing base64 data URI, length:', obj.length);
      return null; // Remove base64 data URIs
    }
    return obj; // Preserve all other primitives
  }
  
  // Handle Blob/File instances
  if (typeof Blob !== 'undefined' && obj instanceof Blob) {
    console.log('🧹 Removing Blob instance, size:', obj.size);
    return null;
  }
  if (typeof File !== 'undefined' && obj instanceof File) {
    console.log('🧹 Removing File instance, name:', obj.name);
    return null;
  }
  if (typeof Date !== 'undefined' && obj instanceof Date) return obj; // Preserve dates
  
  // Handle arrays - CRITICAL: Preserve array structure
  if (Array.isArray(obj)) {
    const cleanedArray = obj.map(item => deepCloneAndCleanBlobs(item)).filter(item => item !== null);
    console.log(`🔄 Processing array: ${obj.length} → ${cleanedArray.length} items`);
    return cleanedArray;
  }
  
  // Handle objects - CRITICAL: Preserve object structure
  const cleaned: any = {};
  let removedFields = 0;
  let preservedFields = 0;
  
  for (const [key, value] of Object.entries(obj)) {
    // Only skip blob-specific fields
    const keyLower = key.toLowerCase();
    if (keyLower === 'imagedata' || keyLower === 'imageblob' || keyLower === 'blob') {
      // Only remove if it's actually blob data, not structural data
      if (typeof value === 'string' && (value.startsWith('data:') || value.length > 1000)) {
        console.log(`🧹 Removing blob field '${key}', length:`, typeof value === 'string' ? value.length : 'non-string');
        removedFields++;
        continue; // Skip large blob-like strings
      }
      if (value instanceof Blob || value instanceof File) {
        console.log(`🧹 Removing blob instance field '${key}'`);
        removedFields++;
        continue; // Skip actual blob instances
      }
    }
    
    // Recursively clean the value while preserving structure
    const cleanedValue = deepCloneAndCleanBlobs(value);
    if (cleanedValue !== null && cleanedValue !== undefined) {
      cleaned[key] = cleanedValue;
      preservedFields++;
    } else {
      removedFields++;
    }
  }
  
  if (removedFields > 0) {
    console.log(`🔄 Object processing: preserved ${preservedFields}, removed ${removedFields} fields`);
  }
  
  return cleaned;
}

/**
 * Helper functions to extract information from backup data
 */
function getMeasurementCount(data: any): number {
  if (!data) return 0;
  
  // Handle both API and local backup types
  const measurements = data.sedationMeasurements || data.SedationMeasurements || [];
  return Array.isArray(measurements) ? measurements.length : 0;
}

function hasIntraOpData(data: any): boolean {
  if (!data) return false;
  
  // Handle both API and local backup types
  const intraOpData = data.intraOppState || data.IntraOppState;
  return !!intraOpData;
}
